import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Copy, Trash2, Users, CheckCircle2, XCircle, Ban, BarChart3, Video, Paperclip, Film, Link as LinkIcon, Download, ExternalLink, BookOpen, Award, FileText, Smile, Star, CalendarCheck } from "lucide-react";
import LiveRatingsPanel from "@/components/LiveRatingsPanel";
import { ResourcePicker } from "@/components/ResourcePicker";
import { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { todayISO } from "@/lib/dateFormat";
import { LIVE_TYPES as TYPES } from "@/lib/liveTypes";
import LiveSessionCard from "@/components/LiveSessionCard";

const LEVELS = ["A1", "A2", "B1", "B2", "C1"];

const STATUS_STYLES = {
  proposed: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
  rejected: "bg-slate-200 text-slate-600",
  cancelled: "bg-red-100 text-red-700",
};

const emptyForm = () => ({
  title: "", description: "", activity_type: "conversation", levels: [],
  date: todayISO(), start_time: "09:00", end_time: "10:00",
  capacity: 15, meet_link: "", teacher_id: "",
});

export default function LiveSessions() {
  const { t, lang } = useI18n();
  const locale = lang === "en" ? "en-US" : "es-CR";
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin" || user?.role === "academic_director";
  const [sessions, setSessions] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [onlyMine, setOnlyMine] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [roster, setRoster] = useState(null);
  const [present, setPresent] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [ratingsOpen, setRatingsOpen] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const [backfillInfo, setBackfillInfo] = useState(null);

  const backfillCalendar = async () => {
    if (!window.confirm(t("live_calendar_backfill_confirm"))) return;
    setBackfilling(true);
    try {
      const r = await api.post("/live-sessions/calendar/backfill");
      const { events, sessions, remaining, reason, upcoming_total } = r.data;
      setBackfillInfo(r.data);
      if (reason === "no_calendars") toast.info(t("live_calendar_backfill_none"));
      else if (sessions === 0) toast.info(t("live_calendar_backfill_nomatch", { total: upcoming_total }));
      else toast.success(t("live_calendar_backfill_done", { events, sessions }) +
        (remaining ? ` ${t("live_calendar_backfill_more", { remaining })}` : ""));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally {
      setBackfilling(false);
    }
  };
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [materialsFor, setMaterialsFor] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: "", title: "" });
  const [uploading, setUploading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [cloning, setCloning] = useState(null); // { session, date, start_time, end_time }

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users", { params: { role: "teacher" } }).then((r) => setTeachers(r.data || []));
  }, [isAdmin]);

  const load = async () => {
    const params = statusFilter === "all" || statusFilter === "past" ? {} : { status: statusFilter };
    // La pestaña "Pasadas" es el historial; las demás muestran solo lo que viene.
    params.past = statusFilter === "past";
    if (onlyMine) params.mine = true;
    const r = await api.get("/live-sessions", { params });
    setSessions(r.data);
  };

  useEffect(() => { load(); }, [statusFilter, onlyMine]);

  // Contador de clases WSS Live que quedan en el mes
  const [monthLeft, setMonthLeft] = useState(null);
  useEffect(() => {
    api.get("/live-sessions/month-count").then((r) => setMonthLeft(r.data)).catch(() => {});
  }, [sessions]);

  const openNew = () => { setEditingId(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      title: s.title || "", description: s.description || "", activity_type: s.activity_type || "conversation",
      levels: s.levels || [], date: s.date, start_time: s.start_time, end_time: s.end_time,
      capacity: s.capacity || 15, meet_link: s.meet_link || "", teacher_id: s.teacher_id || "",
    });
    setOpen(true);
  };

  const toggleLevel = (lv) =>
    setForm((f) => ({ ...f, levels: f.levels.includes(lv) ? f.levels.filter((x) => x !== lv) : [...f.levels, lv] }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error(t("live_title_required")); return; }
    try {
      if (editingId) await api.patch(`/live-sessions/${editingId}`, form);
      else await api.post("/live-sessions", form);
      toast.success(isAdmin || editingId ? t("saved") : t("live_sent_for_approval"));
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("error_generic")); }
  };

  const awardBadge = async (s) => {
    if (!window.confirm(t("live_award_confirm", { title: s.title, attended: s.attended_count ?? 0 }))) return;
    try {
      const r = await api.post("/gamification/award", { badge_id: "pioneer_live", live_session_id: s.id });
      toast.success(t("live_award_done", { n: r.data.awarded, skipped: r.data.skipped }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    }
  };

  const openRoster = async (s) => {
    const r = await api.get(`/live-sessions/${s.id}`);
    setRoster(r.data);
    setPresent(r.data.roster.filter((x) => x.attended).map((x) => x.user_id));
  };

  const saveAttendance = async () => {
    await api.post(`/live-sessions/${roster.session.id}/attendance`, { present_user_ids: present });
    toast.success(t("saved"));
    setRoster(null);
  };

  const uploadMaterial = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/live-sessions/${materialsFor.id}/materials`, fd);
        setMaterialsFor((s) => ({ ...s, materials: [...(s.materials || []), r.data] }));
        ok += 1;
      } catch (err) { toast.error(`${file.name}: ${err?.response?.data?.detail || t("error_generic")}`); }
    }
    if (ok) { toast.success(ok > 1 ? t("live_materials_added", { n: ok }) : t("live_material_added")); load(); }
    setUploading(false);
    e.target.value = "";
  };

  const addLink = async () => {
    if (!linkForm.url.trim()) return;
    try {
      const r = await api.post(`/live-sessions/${materialsFor.id}/materials/link`, linkForm);
      setMaterialsFor((s) => ({ ...s, materials: [...(s.materials || []), r.data] }));
      setLinkForm({ url: "", title: "" });
      toast.success(t("live_material_added"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_generic")); }
  };

  const addResources = async (pickedIds) => {
    setPickerOpen(false);
    try {
      const r = await api.post(`/live-sessions/${materialsFor.id}/materials/resources`, { resource_ids: pickedIds });
      setMaterialsFor((s) => ({ ...s, materials: [...(s.materials || []), ...(r.data.added || [])] }));
      toast.success(t("live_material_added"));
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("error_generic"));
    }
  };

  const removeMaterial = async (mid) => {
    try {
      await api.delete(`/live-sessions/${materialsFor.id}/materials/${mid}`);
      setMaterialsFor((s) => ({ ...s, materials: (s.materials || []).filter((m) => m.id !== mid) }));
      toast.success(t("deleted"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_generic")); }
  };

  const toggleCover = async (mid) => {
    try {
      const r = await api.post(`/live-sessions/${materialsFor.id}/materials/${mid}/cover`);
      setMaterialsFor((s) => ({ ...s, cover_material_id: r.data.cover_material_id }));
      toast.success(r.data.cover_material_id ? t("live_cover_saved") : t("live_cover_cleared"));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t("error_generic")); }
  };

  const openStats = async () => {    const r = await api.get("/live-sessions/stats");
    setStats(r.data);
    setStatsOpen(true);
  };

  const defaultLink = useMemo(() => {
    if (isAdmin) {
      const tt = teachers.find((x) => x.user_id === form.teacher_id);
      return tt ? (tt.meet_link || tt.zoom_link || "") : "";
    }
    return user?.meet_link || user?.zoom_link || "";
  }, [isAdmin, teachers, form.teacher_id, user]);

  return (
    <div>
      <PageHeader
        title={t("live_sessions")}
        subtitle={t("live_sessions_sub")}
        testId="live-sessions-header"
        action={
          <div className="flex gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={openStats} data-testid="live-stats-button">
                <BarChart3 className="h-4 w-4" /> {t("live_stats")}
              </Button>
            )}
            <Button variant="outline" onClick={() => setRatingsOpen(true)} data-testid="live-ratings-button">
              <Smile className="h-4 w-4" /> {t("live_ratings_button")}
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={backfillCalendar} disabled={backfilling} data-testid="live-calendar-backfill">
                <CalendarCheck className="h-4 w-4" /> {t("live_calendar_backfill")}
              </Button>
            )}
            <Button onClick={openNew} data-testid="new-live-session-button">
              <Plus className="h-4 w-4" /> {t("live_new_session")}
            </Button>
          </div>
        }
      />

      {backfillInfo && (
        <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm" data-testid="live-calendar-report">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="font-semibold text-slate-800">{t("live_calendar_report_title")}</div>
              <div className="text-slate-600">
                {t("live_calendar_report_written", { events: backfillInfo.events, sessions: backfillInfo.sessions })}
                {" · "}
                {t("live_calendar_report_classes", { classes: backfillInfo.classes || 0, groups: backfillInfo.groups_total || 0 })}
                {" · "}
                {t("live_calendar_report_upcoming", { total: backfillInfo.upcoming_total || 0 })}
              </div>
              {(backfillInfo.connected_emails || []).length > 0 && (
                <div className="text-slate-500" data-testid="live-calendar-report-connected">
                  {t("live_calendar_report_connected")}: {backfillInfo.connected_emails.join(", ")}
                </div>
              )}
              {(backfillInfo.blocked_by_teacher || []).length > 0 && (
                <div className="text-amber-700" data-testid="live-calendar-report-blocked">
                  {t("live_calendar_report_blocked")}:{" "}
                  {backfillInfo.blocked_by_teacher.map((b) => `${b.teacher_name} (${b.sessions})`).join(", ")}
                </div>
              )}
            </div>
            <button onClick={() => setBackfillInfo(null)} data-testid="live-calendar-report-close"
              className="text-slate-400 hover:text-slate-700"><XCircle className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {["all", "proposed", "published", "cancelled", "rejected", "past"].map((s) => (
          <button
            key={s}
            data-testid={`live-filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              statusFilter === s ? "bg-brand-blue text-white border-[#004aad]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {t(`live_status_${s}`)}
          </button>
        ))}
        <button
          data-testid="live-filter-mine"
          onClick={() => setOnlyMine((v) => !v)}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
            onlyMine ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          {t("live_only_mine")}
        </button>
        {monthLeft && (monthLeft.upcoming > 0 || monthLeft.total > 0) && (
          <span className="ml-auto inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-200 px-3 py-1"
                data-testid="live-month-counter">
            <span className="font-display text-base font-bold text-brand">{monthLeft.upcoming}</span>
            <span className="text-xs text-orange-900/80">
              {t("live_month_summary", { upcoming: monthLeft.upcoming, remaining: monthLeft.remaining, total: monthLeft.total })}
            </span>
          </span>
        )}
      </div>

      {sessions.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-500" data-testid="live-empty">
          {t("no_data")}
        </div>
      )}

      {/* Una sola cuadrícula: cada tarjeta ya lleva su fecha, así siempre se
          aprovechan las dos columnas aunque haya una sesión por día. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="live-sessions-grid">
        {[...sessions]
          .sort((a, b) => (statusFilter === "past"
            ? (b.date + b.start_time).localeCompare(a.date + a.start_time)
            : (a.date + a.start_time).localeCompare(b.date + b.start_time)))
          .map((s) => {
            // Un profe ve toda la agenda, pero solo gestiona sus propias sesiones.
            const canManage = isAdmin || s.teacher_id === user?.user_id;
            return (
              <div key={s.id} data-testid={`live-session-${s.id}`}>
                <LiveSessionCard
                  session={s}
                  locale={locale}
                  badges={
                    <>
                      {s.is_past && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-700 text-white"
                              data-testid={`live-completed-badge-${s.id}`}>
                          {t("live_status_completed")}
                        </span>
                      )}
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLES[s.status] || "bg-white/90 text-slate-700"}`}>
                        {t(`live_status_${s.status}`)}
                      </span>
                    </>
                  }
                    actions={
                      <div className="space-y-2">
                        {s.is_past && (
                          <div className="text-xs text-slate-500" data-testid={`live-past-summary-${s.id}`}>
                            {t("live_past_summary", { booked: s.booked_count, attended: s.attended_count })}
                          </div>
                        )}
                        {!s.meet_link && !s.is_past && (
                          <div className="text-xs text-amber-700">{t("live_missing_link")}</div>
                        )}
                        {s.link_from_profile && !s.is_past && (
                          <div className="text-xs text-slate-400">{t("live_link_from_profile")}</div>
                        )}
                        {s.rejection_reason && (
                          <div className="text-xs text-red-600">{t("live_rejected_reason")}: {s.rejection_reason}</div>
                        )}
                        {s.waitlist_count > 0 && (
                          <div className="text-xs text-slate-500">{t("live_waitlist")}: {s.waitlist_count}</div>
                        )}
                        <div className="flex items-center gap-1 flex-wrap border-t border-slate-100 pt-2">
                          {s.meet_link && s.status === "published" && !s.is_past && (
                            <a href={s.meet_link} target="_blank" rel="noreferrer" data-testid={`join-live-${s.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1.5 bg-brand-blue text-white hover:bg-[#003a8c] transition-colors mr-1">
                              <Video className="h-3.5 w-3.5" /> {t("live_join")}
                            </a>
                          )}
                          {isAdmin && s.status === "proposed" && (
                            <>
                              <Button size="sm" data-testid={`approve-live-${s.id}`}
                                onClick={() => act(() => api.post(`/live-sessions/${s.id}/approve`), t("live_approved"))}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> {t("live_approve")}
                              </Button>
                              <Button size="sm" variant="outline" data-testid={`reject-live-${s.id}`}
                                onClick={() => { setRejecting(s); setRejectReason(""); }}>
                                <XCircle className="h-3.5 w-3.5" /> {t("live_reject")}
                              </Button>
                            </>
                          )}
                          {canManage && (
                            <>
                          <Button variant="ghost" size="icon" title={t("live_materials")} data-testid={`materials-live-${s.id}`}
                            onClick={() => setMaterialsFor(s)}>
                            <Paperclip className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" title={t("live_roster")} data-testid={`roster-live-${s.id}`} onClick={() => openRoster(s)}>
                            <Users className="h-3.5 w-3.5" />
                          </Button>
                          {s.is_past && (s.attended_count ?? 0) > 0 && (
                            <Button variant="ghost" size="icon" title={t("live_award_badge")} data-testid={`award-live-${s.id}`}
                              className="text-amber-600" onClick={() => awardBadge(s)}>
                              <Award className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" title={t("edit")} data-testid={`edit-live-${s.id}`} onClick={() => openEdit(s)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" title={t("live_clone")} data-testid={`duplicate-live-${s.id}`}
                            onClick={() => setCloning({ session: s, date: todayISO(), start_time: s.start_time, end_time: s.end_time })}>
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                            </>
                          )}
                          {canManage && s.status !== "cancelled" && (
                            <Button variant="ghost" size="icon" className="text-amber-600" title={t("live_cancel_session")} data-testid={`cancel-live-${s.id}`}
                              onClick={() => { if (window.confirm(t("live_cancel_confirm"))) act(() => api.post(`/live-sessions/${s.id}/cancel`), t("live_cancelled_ok")); }}>
                              <Ban className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canManage && (
                            <Button variant="ghost" size="icon" className="text-red-600" title={t("delete")} data-testid={`delete-live-${s.id}`}
                              onClick={() => { if (window.confirm(t("confirm_delete"))) act(() => api.delete(`/live-sessions/${s.id}`), t("deleted")); }}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    }
                  />
                </div>
            );
          })}
      </div>

      {/* Create / edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{editingId ? t("edit") : t("live_new_session")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("live_topic")}</Label>
              <Input data-testid="live-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <Label>{t("description")}</Label>
              <Textarea rows={3} data-testid="live-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            {isAdmin && (
              <div>
                <Label>{t("live_assigned_teacher")}</Label>
                <Select value={form.teacher_id || ""} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger data-testid="live-teacher-select"><SelectValue placeholder={t("live_pick_teacher")} /></SelectTrigger>
                  <SelectContent>
                    {teachers.map((tt) => (
                      <SelectItem key={tt.user_id} value={tt.user_id}>{tt.name || tt.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>{t("live_activity_type")}</Label>
                <Select value={form.activity_type} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
                  <SelectTrigger data-testid="live-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map((ty) => <SelectItem key={ty} value={ty}>{t(`live_type_${ty}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("live_capacity")}</Label>
                <Input type="number" min={1} data-testid="live-capacity-input" value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} />
              </div>
            </div>
            <div>
              <Label>{t("live_levels")}</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="live-levels-group">
                {LEVELS.map((lv) => (
                  <button
                    key={lv}
                    type="button"
                    data-testid={`live-level-${lv}`}
                    onClick={() => toggleLevel(lv)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      form.levels.includes(lv) ? "bg-brand-blue text-white border-[#004aad]" : "bg-white text-slate-600 border-slate-200"
                    }`}
                  >
                    {lv}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">{t("live_levels_hint")}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>{t("date")}</Label>
                <Input type="date" data-testid="live-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label>{t("start_time")}</Label>
                <Input type="time" data-testid="live-start-input" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div>
                <Label>{t("end_time")}</Label>
                <Input type="time" data-testid="live-end-input" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("live_meet_link")}</Label>
              <Input data-testid="live-link-input" placeholder="https://meet.google.com/... · https://zoom.us/j/..." value={form.meet_link}
                onChange={(e) => setForm({ ...form, meet_link: e.target.value })} />
              <p className="text-xs text-slate-500 mt-1">{isAdmin ? t("live_meet_link_hint_admin") : t("live_meet_link_hint")}</p>
              {!form.meet_link && (
                defaultLink ? (
                  <p className="text-xs text-emerald-700 mt-1 break-all" data-testid="live-default-link-preview">
                    {t("live_default_link")}: {defaultLink}
                  </p>
                ) : (
                  <p className="text-xs text-amber-700 mt-1" data-testid="live-no-default-link">
                    {t("live_no_default_link")}
                  </p>
                )
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-live-session-button">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Roster + attendance */}
      <Dialog open={!!roster} onOpenChange={(v) => !v && setRoster(null)}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("live_roster")} · {roster?.session?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="live-roster-list">
            {(roster?.roster || []).length === 0 && <div className="text-sm text-slate-500">{t("live_no_bookings")}</div>}
            {(roster?.roster || []).map((r) => (
              <label key={r.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-slate-50" data-testid={`roster-row-${r.user_id}`}>
                <Checkbox
                  checked={present.includes(r.user_id)}
                  onCheckedChange={(v) => setPresent((p) => (v ? [...p, r.user_id] : p.filter((x) => x !== r.user_id)))}
                />
                <div className="min-w-0">
                  <div className="text-sm text-slate-900 truncate">{r.user_name}</div>
                  <div className="text-xs text-slate-500">
                    {r.status === "waitlisted" ? t("live_waitlist") : t("live_confirmed")}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoster(null)}>{t("cancel")}</Button>
            <Button onClick={saveAttendance} data-testid="save-live-attendance-button">{t("live_save_attendance")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject reason */}
      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("live_reject")}</DialogTitle></DialogHeader>
          <Textarea rows={3} data-testid="live-reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t("live_reject_reason_ph")} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>{t("cancel")}</Button>
            <Button data-testid="confirm-reject-live" onClick={async () => {
              const id = rejecting.id; setRejecting(null);
              await act(() => api.post(`/live-sessions/${id}/reject`, { reason: rejectReason }), t("saved"));
            }}>{t("confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ¿Les está gustando? */}
      <Dialog open={ratingsOpen} onOpenChange={setRatingsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("live_ratings_title")}</DialogTitle></DialogHeader>
          {ratingsOpen && <LiveRatingsPanel />}
        </DialogContent>
      </Dialog>

      {/* Materials */}      <Dialog open={!!materialsFor} onOpenChange={(v) => !v && setMaterialsFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("live_materials")} · {materialsFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">{t("live_upload_material")}</Label>
              <Input type="file" multiple
                accept="image/*,video/mp4,video/webm,video/quicktime,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,application/pdf"
                data-testid="live-material-file" disabled={uploading} onChange={uploadMaterial} className="mt-1.5" />
              <p className="text-xs text-slate-500 mt-1">{t("live_material_hint")}</p>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">{t("live_add_link")}</Label>
              <div className="flex gap-2 mt-1.5">
                <Input placeholder="https://youtube.com/..." data-testid="live-material-link-url"
                  value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} />
                <Button onClick={addLink} data-testid="live-material-link-add">{t("add")}</Button>
              </div>
            </div>
            <div className="border-t border-slate-100 pt-3">
              <Label className="text-xs">{t("live_from_resources")}</Label>
              <Button variant="outline" className="mt-1.5 w-full justify-start" data-testid="live-material-from-resources"
                onClick={() => setPickerOpen(true)}>
                <BookOpen className="h-4 w-4" /> {t("live_pick_resources")}
              </Button>
              <p className="text-xs text-slate-500 mt-1">{t("live_from_resources_hint")}</p>
            </div>
            <div className="space-y-2" data-testid="live-materials-list">
              {(materialsFor?.materials || []).length === 0 && (
                <div className="text-sm text-slate-500">{t("live_no_materials")}</div>
              )}
              {(materialsFor?.materials || []).length > 0 && (
                <p className="text-xs text-slate-500">{t("live_cover_hint")}</p>
              )}
              {(materialsFor?.materials || []).map((m) => {
                const isRes = m.type === "resource";
                const resUrl = m.url || `${API_BASE}/api/resources/${m.resource_id}/file`;
                const viewUrl = isRes ? resUrl : m.type === "link" ? m.url : `${API_BASE}/api/files/${m.file_id}/view`;
                const dlUrl = isRes ? resUrl : m.type === "link" ? m.url : `${API_BASE}/api/files/${m.file_id}/download`;
                return (
                  <div key={m.id} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2" data-testid={`material-row-${m.id}`}>
                    {m.type === "image" ? (
                      <a href={viewUrl} target="_blank" rel="noreferrer">
                        <img src={viewUrl} alt={m.title} className="h-14 w-14 object-cover rounded-md border border-slate-200" />
                      </a>
                    ) : (
                      <div className="h-14 w-14 rounded-md bg-slate-100 flex items-center justify-center">
                        {isRes ? <BookOpen className="h-5 w-5 text-slate-500" />
                          : m.type === "video" ? <Film className="h-5 w-5 text-slate-500" />
                          : m.type === "document" ? <FileText className="h-5 w-5 text-rose-500" />
                          : <LinkIcon className="h-5 w-5 text-slate-500" />}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-900 truncate">{m.title}</div>
                      <div className="text-xs text-slate-500">
                        {isRes ? `${t("live_material_type_resource")} · ${m.resource_kind}` : t(`live_material_type_${m.type}`)}{m.size ? ` · ${(m.size / 1024 / 1024).toFixed(1)} MB` : ""}
                      </div>
                    </div>
                    <a href={viewUrl} target="_blank" rel="noreferrer" title={t("live_open")} data-testid={`material-open-${m.id}`}
                      className="p-2 text-slate-500 hover:text-brand-blue"><ExternalLink className="h-4 w-4" /></a>
                    {m.type !== "link" && !(isRes && !m.has_file) && (
                      <a href={dlUrl} title={t("download")} data-testid={`material-download-${m.id}`}
                        className="p-2 text-slate-500 hover:text-brand-blue"><Download className="h-4 w-4" /></a>
                    )}
                    {(m.type === "image" || m.type === "resource") && (
                      <button onClick={() => toggleCover(m.id)} data-testid={`material-cover-${m.id}`}
                        title={materialsFor?.cover_material_id === m.id ? t("live_cover_is") : t("live_cover_set")}
                        className={`p-2 transition-colors ${materialsFor?.cover_material_id === m.id ? "text-amber-500" : "text-slate-300 hover:text-amber-500"}`}>
                        <Star className="h-4 w-4" fill={materialsFor?.cover_material_id === m.id ? "currentColor" : "none"} />
                      </button>
                    )}
                    <button onClick={() => removeMaterial(m.id)} title={t("delete")} data-testid={`material-delete-${m.id}`}
                      className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recursos del banco como material */}
      <ResourcePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onConfirm={addResources}
        excludeIds={(materialsFor?.materials || []).filter((m) => m.resource_id).map((m) => m.resource_id)}
      />

      {/* Clonar una sesión: se copia todo y solo se cambian fecha y hora */}
      <Dialog open={!!cloning} onOpenChange={(v) => !v && setCloning(null)}>
        <DialogContent aria-describedby={undefined} data-testid="clone-live-dialog">
          <DialogHeader><DialogTitle>{t("live_clone")} · {cloning?.session?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("live_clone_hint")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>{t("date")}</Label>
              <Input type="date" data-testid="clone-date-input" value={cloning?.date || ""}
                onChange={(e) => setCloning({ ...cloning, date: e.target.value })} />
            </div>
            <div>
              <Label>{t("start_time")}</Label>
              <Input type="time" data-testid="clone-start-input" value={cloning?.start_time || ""}
                onChange={(e) => setCloning({ ...cloning, start_time: e.target.value })} />
            </div>
            <div>
              <Label>{t("end_time")}</Label>
              <Input type="time" data-testid="clone-end-input" value={cloning?.end_time || ""}
                onChange={(e) => setCloning({ ...cloning, end_time: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloning(null)}>{t("cancel")}</Button>
            <Button data-testid="confirm-clone-live" onClick={async () => {
              const { session, date, start_time, end_time } = cloning;
              setCloning(null);
              await act(() => api.post(`/live-sessions/${session.id}/duplicate`, { date, start_time, end_time }),
                        t("live_cloned"));
            }}>{t("live_clone")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{t("live_stats")}</DialogTitle></DialogHeader>
          {stats && (
            <div className="space-y-5" data-testid="live-stats-content">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["live_stat_sessions", stats.total_sessions],
                  ["live_stat_bookings", stats.total_bookings],
                  ["live_stat_attended", stats.total_attended],
                  ["live_stat_rate", `${stats.attendance_rate}%`],
                ].map(([k, v]) => (
                  <div key={k} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{t(k)}</div>
                    <div className="font-display text-xl font-bold text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">{t("live_top_sessions")}</div>
                <div className="space-y-1">
                  {stats.top_sessions.map((s) => (
                    <div key={s.id} className="flex justify-between text-sm border-b border-slate-100 py-1">
                      <span className="truncate">{s.title} · {s.date} {s.start_time}</span>
                      <span className="text-slate-500">{s.booked}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <div className="text-sm font-semibold mb-2">{t("live_demand_by_level")}</div>
                  {Object.entries(stats.bookings_by_level).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm"><span>{k === "all" ? t("live_all_levels") : k}</span><span className="text-slate-500">{v}</span></div>
                  ))}
                </div>
                <div>
                  <div className="text-sm font-semibold mb-2">{t("live_demand_by_hour")}</div>
                  {Object.entries(stats.bookings_by_hour).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm"><span>{k}</span><span className="text-slate-500">{v}</span></div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-sm font-semibold mb-2">{t("live_top_students")}</div>
                {stats.top_students.map((s) => (
                  <div key={s.user_id} className="flex justify-between text-sm border-b border-slate-100 py-1">
                    <span className="truncate">{s.name}</span>
                    <span className="text-slate-500">{s.bookings} · {s.attended} {t("live_attended_short")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
